import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Input, Button, message, Typography } from "antd";
import { API_BASE_URL } from "../config/apiConfig";
import { useTranslation } from "react-i18next";
import HomeButton from "../components/HomeButton";

const { TextArea } = Input;
const { Title } = Typography;

const TextSummarization: React.FC = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleTextSummarization = async (values: { inputText: string }) => {
    setLoading(true);
    setSummary("");
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${API_BASE_URL}/zhadiger/api/text-summarization/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ texts: values.inputText }),
        }
      );

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data:")) {
            const content = line.substring(5);
            setSummary((prev) => prev + content);
          }
        }
      }

      message.success("Text has been summarized!");
    } catch (error) {
      console.error("Streaming error:", error);
      message.error("Error summarizing text");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 ml-4 pt-4 ">
        <HomeButton />
      </div>
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white border border-gray-200 rounded-lg shadow-md flex-grow">
        <Title level={2} className="text-center font-bold text-gray-700">
          {t("text_summarization") || "Text Summarization"}
        </Title>

        <Formik
          initialValues={{ inputText: "" }}
          onSubmit={handleTextSummarization}
        >
          {({ handleSubmit }) => (
            <Form className="space-y-4">
              <Field name="inputText">
                {({ field }: { field: any }) => (
                  <TextArea
                    {...field}
                    placeholder={t("enter_text")}
                    rows={4}
                    disabled={loading}
                    className="p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </Field>

              <Button
                type="primary"
                onClick={() => handleSubmit()}
                loading={loading}
                className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 transition duration-200"
              >
                {t("summarize") || "Summarize"}
              </Button>
            </Form>
          )}
        </Formik>

        {summary && (
          <div className="mt-6 text-center">
            <Title level={4} className="text-gray-600">
              {t("summary") || "Summary"}
            </Title>
            <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg whitespace-pre-wrap text-left">
              {summary}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TextSummarization;
